import type { LucideIcon } from 'lucide-react'

import DefaultUser from 'src/shared/assets/images/default-user.png'
import { cn } from 'src/shared/utils/cn'

interface OverlappedUsersProps {
  firstUserPhoto?: string
  secondUserPhoto?: string
  icon?: LucideIcon
  centerColor?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl'
  className?: string
}

export const OverlappedUsers = ({
  firstUserPhoto,
  secondUserPhoto,
  icon: Icon,
  centerColor = 'bg-secondary',
  size = 'md',
  className,
}: OverlappedUsersProps) => {
  const sizeClasses = {
    sm: {
      container: 'size-16',
      icon: 16,
      iconContainer: 'p-1.5',
    },
    md: {
      container: 'size-20',
      icon: 18,
      iconContainer: 'p-2',
    },
    lg: {
      container: 'size-28',
      icon: 24,
      iconContainer: 'p-3',
    },
    xl: {
      container: 'size-32',
      icon: 28,
      iconContainer: 'p-4',
    },
    '2xl': {
      container: 'size-40',
      icon: 30,
      iconContainer: 'p-4',
    },
    '3xl': {
      container: 'size-48',
      icon: 36,
      iconContainer: 'p-5',
    },
    '4xl': {
      container: 'size-56',
      icon: 36,
      iconContainer: 'p-5',
    },
    '5xl': {
      container: 'size-64',
      icon: 48,
      iconContainer: 'p-8',
    },
    '6xl': {
      container: 'size-72',
      icon: 72,
      iconContainer: 'p-9',
    },
    '7xl': {
      container: 'size-80',
      icon: 80,
      iconContainer: 'p-10',
    },
  }

  return (
    <div className={cn('relative flex items-center justify-center', className)}>
      {/* First user image */}
      <div
        className={cn(
          'relative z-10 -mr-2 -rotate-12 overflow-hidden rounded-2xl border-4 border-white bg-background',
          sizeClasses[size].container,
        )}
      >
        <img src={firstUserPhoto || DefaultUser} alt={'First user'} className="size-full object-cover" />
      </div>
      {/* Second user image */}
      <div
        className={cn(
          'relative z-0 -ml-2 rotate-12 overflow-hidden rounded-2xl border-4 border-white bg-background',
          sizeClasses[size].container,
        )}
      >
        <img src={secondUserPhoto || DefaultUser} alt={'Second user'} className="size-full object-cover" />
      </div>
      {/* Center icon */}
      {Icon && (
        <div
          className={cn(
            'absolute left-1/2 top-3/4 z-20 -translate-x-1/2 -translate-y-1/2 rounded-2xl border-4 border-white',
            centerColor,
            sizeClasses[size].iconContainer,
          )}
        >
          <Icon className="text-secondary-foreground" size={sizeClasses[size].icon} />
        </div>
      )}
    </div>
  )
}
